import { type FC, useEffect, useState } from 'react';

import { getUserLabel } from '@iconik-io/core/src/get_user_label';
import {
  httpGetUsersBasic,
  httpGetUsersByUserId,
  type UsersUserBasicElasticSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/users';

import UserNameValue, {
  type UserNameOption,
} from '../../../../components/select_field_custom_values/select_field_username_value/select_field_username_value';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../store/react_redux_typed';
import { selectUserRoles } from '../../../../utils/get_user_roles';
import { prepareQueryParameter } from '../../../../utils/search_utils/search_utils';
import type { SelectOption } from '../../../select_field/types';
import {
  type LoadOptionsResult,
  resolveRemoteResponse,
} from '../../../select_field/utils';
import SelectFieldWrapper, {
  type SelectFieldWrapperProps,
} from '../select/select';
import SelectFieldMultiWrapper, {
  type SelectFieldMultiWrapperProps,
} from '../select_multi/select_multi';

type Props = SelectFieldWrapperProps & {
  isMulti?: false;
  filterOptionsPredicate?: (
    object: UsersUserBasicElasticSchemaWithIdNullable,
  ) => boolean;
  isSystemDomainUserIncluded?: boolean;
};

type MultiProps = SelectFieldMultiWrapperProps<
  UserNameOption,
  UserNameOption,
  UserNameOption
> & {
  isMulti: true;
  filterOptionsPredicate?: (
    object: UsersUserBasicElasticSchemaWithIdNullable,
  ) => boolean;
  isSystemDomainUserIncluded?: boolean;
};

const defaultFilterOptionsPredicate = (
  _object: UsersUserBasicElasticSchemaWithIdNullable,
) => true;

const filterOptionWithListRole = (
  option: UserNameOption,
  filterValue: string,
): boolean =>
  Boolean(
    option.label?.toLowerCase().includes(filterValue.toLowerCase()) ||
      option.email?.toLowerCase().includes(filterValue.toLowerCase()),
  );

const filterOptionWithoutListRole = (
  option: UserNameOption,
  filterValue: string,
): boolean => option.email === filterValue;

const processNewOptionValue = (option: UserNameOption) => ({
  ...option,
  label: option.label.trim(),
});

export const SelectUsersFieldWrapper: FC<Props | MultiProps> = (props) => {
  const {
    creatorText = 'Type + Enter to add an email',
    creatorAction = 'Add email',
    canClear = true,
    filterOptionsPredicate = defaultFilterOptionsPredicate,
    options,
    isSystemDomainUserIncluded = false,
  } = props;
  const userRoles = useAppSelector(selectUserRoles);
  const dispatch = useAppDispatch();
  const filterOption = !userRoles.webCanListUsers
    ? filterOptionWithoutListRole
    : filterOptionWithListRole;
  const currentUser = useAppSelector((state) => state.authReducer.userData);
  const [systemUserName, setSystemUserName] = useState('System user');

  useEffect(() => {
    if (isSystemDomainUserIncluded && currentUser.system_domain_id) {
      dispatch(httpGetUsersByUserId(currentUser.system_domain_id)).then(
        (response) => {
          setSystemUserName(getUserLabel(response.body));
        },
      );
    }
  }, [currentUser.system_domain_id, isSystemDomainUserIncluded]);

  // baseOptions gets merged with result of handleLoadOptions below, by SelectFieldWrapper.
  let baseOptions = options ?? [];
  if (isSystemDomainUserIncluded && currentUser.system_domain_id) {
    baseOptions = baseOptions.concat({
      value: currentUser.system_domain_id,
      label: systemUserName,
    });
  }

  const handleLoadOptions = (query: string): Promise<LoadOptionsResult> =>
    dispatch(
      httpGetUsersBasic({
        query: prepareQueryParameter(query),
        sort: 'first_name,asc',
        per_page: 100,
      }),
    ).then(
      resolveRemoteResponse((response) => {
        const { ignoreIds = [] } = props;
        const responseOptions = response.body.objects
          .filter(filterOptionsPredicate)
          .filter((user) => !ignoreIds.includes(user.id))
          .map((user) => ({
            label: getUserLabel(user),
            value: user.id,
            email: user.email,
            photo: user.photo_small,
          }));

        const result = responseOptions.filter((option) => {
          // Hide user names for users without webCanListUsers role
          // and only show when we have match to email
          if (!userRoles.webCanListUsers) {
            return Boolean(option && option.email && option.email === query);
          }
          return true;
        });

        return result;
      }),
    );

  const handleLoadSelectedOptions = async (
    ids: string[],
  ): Promise<SelectOption[]> => {
    return dispatch(
      httpGetUsersBasic({
        ids: ids.join(','),
        per_page: 100,
      }),
    ).then((response): SelectOption[] =>
      response.body.objects.map((user) => ({
        label: getUserLabel(user),
        value: user.id,
        email: user.email,
        photo: user.photo_small,
      })),
    );
  };

  const sharedProps = {
    options: baseOptions,
    creatorText,
    creatorAction,
    canClear,
    preloadOptions: true,
    valueComponent: UserNameValue,
    loadOptions: handleLoadOptions,
    loadSelectedOptions: handleLoadSelectedOptions,
    filterOption,
    processNewOptionValue,
  };

  if (props.isMulti) {
    return <SelectFieldMultiWrapper {...props} {...sharedProps} />;
  }

  return <SelectFieldWrapper {...props} {...sharedProps} />;
};

export default SelectUsersFieldWrapper;
